import type {
  BasisOption,
  BasisOptionsResponse,
  DatabookJobStatus,
  PrebuildReviewState,
} from "@/lib/api/databook";

export type BasisOptionsLoadState =
  | { status: "loading" }
  | {
      status: "loaded";
      builderContextId: string | null;
      sourceRevisionId: string | null;
      legalStructureRevisionId: string | null;
      legalStructureFingerprint: string | null;
      presentationCurrency: string | null;
      options: BasisOption[];
    }
  | { status: "empty"; sourceRevisionId: string | null }
  | { status: "unavailable"; sourceRevisionId: string | null }
  | { status: "pending_refresh"; sourceRevisionId: string | null }
  | {
      status: "refresh_failed";
      sourceRevisionId: string | null;
      error: string | null;
    }
  | {
      status: "needs_review";
      sourceRevisionId: string | null;
      issues: NonNullable<BasisOptionsResponse["issues"]>;
    }
  | {
      status: "failed";
      sourceRevisionId: string | null;
      invalidArtifact: boolean;
    };

export interface BasisSelectionState {
  builderContextId: string | null;
  sourceRevisionId: string | null;
  legalStructureRevisionId: string | null;
  legalStructureFingerprint: string | null;
  optionId: string | null;
}

export function basisLoadState(
  response: BasisOptionsResponse,
): BasisOptionsLoadState {
  if (response.state === "loaded") {
    return {
      status: "loaded",
      builderContextId: response.builder_context_id,
      sourceRevisionId: response.source_revision_id,
      legalStructureRevisionId: response.legal_structure_revision_id,
      legalStructureFingerprint: response.legal_structure_fingerprint,
      presentationCurrency: response.presentation_currency,
      options: response.options,
    };
  }
  if (response.state === "empty") {
    return { status: "empty", sourceRevisionId: response.source_revision_id };
  }
  if (response.state === "unavailable") {
    return {
      status: "unavailable",
      sourceRevisionId: response.source_revision_id,
    };
  }
  if (response.state === "pending_refresh") {
    return {
      status: "pending_refresh",
      sourceRevisionId: response.source_revision_id,
    };
  }
  if (response.state === "refresh_failed") {
    return {
      status: "refresh_failed",
      sourceRevisionId: response.source_revision_id,
      error: response.error,
    };
  }
  if (response.state === "needs_review") {
    return {
      status: "needs_review",
      sourceRevisionId: response.source_revision_id,
      issues: response.issues ?? [],
    };
  }
  return {
    status: "failed",
    sourceRevisionId: response.source_revision_id,
    invalidArtifact: true,
  };
}

export function shouldRefreshBasis(response: BasisOptionsResponse): boolean {
  return response.state === "pending_refresh"
    && (
      response.basis_status === "agent_required"
      || response.basis_status == null
    );
}

export function reconcileBasisSelection(
  previous: BasisSelectionState,
  response: BasisOptionsResponse,
  currentJob: Pick<
    DatabookJobStatus,
    "builder_context_id" | "basis_id"
  > | null = null,
): BasisSelectionState {
  const options = response.state === "loaded" ? response.options : [];
  const sameRevision =
    previous.builderContextId === response.builder_context_id;
  const previousStillExists =
    sameRevision && options.some((option) => option.id === previous.optionId);
  if (previousStillExists) return previous;
  const jobBasisStillExists = (
    currentJob?.builder_context_id === response.builder_context_id
    && options.some((option) => option.id === currentJob.basis_id)
  );
  if (jobBasisStillExists) {
    return {
      builderContextId: response.builder_context_id,
      sourceRevisionId: response.source_revision_id,
      legalStructureRevisionId: response.legal_structure_revision_id,
      legalStructureFingerprint: response.legal_structure_fingerprint,
      optionId: currentJob.basis_id,
    };
  }
  const recommended = options.find((option) => option.recommended);
  return {
    builderContextId: response.builder_context_id,
    sourceRevisionId: response.source_revision_id,
    legalStructureRevisionId: response.legal_structure_revision_id,
    legalStructureFingerprint: response.legal_structure_fingerprint,
    optionId: recommended?.id ?? (options.length === 1 ? options[0].id : null),
  };
}

export function canBuildWithBasis(
  reviewState: PrebuildReviewState | undefined,
  basis: BasisOptionsLoadState,
  selection: BasisSelectionState,
): boolean {
  return reviewState === "ready"
    && basis.status === "loaded"
    && Boolean(selection.builderContextId && selection.sourceRevisionId
      && selection.legalStructureRevisionId && selection.legalStructureFingerprint)
    && selection.builderContextId === basis.builderContextId
    && selection.sourceRevisionId === basis.sourceRevisionId
    && selection.legalStructureRevisionId === basis.legalStructureRevisionId
    && selection.legalStructureFingerprint === basis.legalStructureFingerprint
    && basis.options.some((option) => option.id === selection.optionId);
}
